import { useState, useEffect, useRef, useCallback } from "react";
import { Columns2, Grid2x2, Maximize2, LayoutGrid, Volume2, VolumeX, ChevronDown } from "lucide-react";

/**
 * SceneComposer — Combines multiple sources into a single split-screen layout.
 * Layouts: fullscreen (1), split-2 (side-by-side), split-3, grid-2x2.
 * Renders to a canvas for PREVIEW/PROGRAM output.
 *
 * Props:
 *   sources: array of all available broadcast sources
 *   cameraStreams: object of sourceId -> {stream, audioEnabled}
 *   onSceneReady: callback(sceneConfig) when scene is prepared
 *   onCancel: callback to close the composer
 */

const LAYOUTS = [
  { id: "fullscreen", label: "Plein ecran", icon: Maximize2, slots: 1, grid: "1fr" },
  { id: "split-2", label: "2 cotes", icon: Columns2, slots: 2, grid: "1fr 1fr" },
  { id: "split-3", label: "3 sources", icon: LayoutGrid, slots: 3, grid: "1fr 1fr" },
  { id: "grid-4", label: "Grille 2x2", icon: Grid2x2, slots: 4, grid: "1fr 1fr" },
];

export default function SceneComposer({ sources = [], cameraStreams = {}, onSceneReady, onCancel }) {
  const [layout, setLayout] = useState("split-2");
  const [slots, setSlots] = useState(Array(4).fill(null)); // sourceId per slot
  const [audioSlot, setAudioSlot] = useState(0); // which slot has audio enabled
  const [sceneName, setSceneName] = useState("Scene 1");

  const layoutDef = LAYOUTS.find(l => l.id === layout) || LAYOUTS[1];
  const slotCount = layoutDef.slots;

  const setSlotSource = (slotIndex, sourceId) => {
    setSlots(prev => {
      const next = [...prev];
      next[slotIndex] = sourceId;
      return next;
    });
  };

  // Get a source by ID
  const getSource = (id) => sources.find(s => s.id === id);

  // Build scene config and send to parent
  const prepareScene = () => {
    const activeSlots = [];
    for (let i = 0; i < slotCount; i++) {
      if (slots[i]) {
        const src = getSource(slots[i]);
        if (src) {
          activeSlots.push({
            index: i,
            source_id: src.id,
            source_type: src.type,
            source_name: src.name,
            config: src.config,
            has_audio: i === audioSlot,
          });
        }
      }
    }

    if (activeSlots.length === 0) return;

    onSceneReady({
      type: "scene",
      name: sceneName,
      config: {
        layout: layout,
        slot_count: slotCount,
        slots: activeSlots,
        audio_slot: audioSlot,
      },
    });
  };

  // Render slot grid
  const renderSlotGrid = () => {
    const slotElements = [];
    for (let i = 0; i < slotCount; i++) {
      const selectedSrc = slots[i] ? getSource(slots[i]) : null;
      slotElements.push(
        <div
          key={i}
          className={`relative border-2 rounded-lg overflow-hidden transition-all
            ${slots[i] ? "border-[#0099CC]/50 bg-[#0B1120]" : "border-dashed border-[#2A3441] bg-[#060B14] hover:border-[#4A5568]"}`}
          style={{ minHeight: slotCount <= 2 ? "120px" : "80px" }}
          data-testid={`scene-slot-${i}`}
        >
          {selectedSrc ? (
            <div className="w-full h-full flex flex-col">
              <SlotPreview source={selectedSrc} cameraStreams={cameraStreams} audioEnabled={i === audioSlot} />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1.5 flex items-center justify-between">
                <span className="text-[9px] text-white font-medium truncate">{selectedSrc.name}</span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setAudioSlot(i)}
                    className={`p-0.5 rounded ${i === audioSlot ? "text-[#10B981]" : "text-[#64748b]"}`}
                    title={i === audioSlot ? "Audio actif" : "Activer l'audio"}
                  >
                    {i === audioSlot ? <Volume2 className="w-2.5 h-2.5" /> : <VolumeX className="w-2.5 h-2.5" />}
                  </button>
                  <button onClick={() => setSlotSource(i, null)} className="text-red-400 hover:text-red-300 p-0.5">
                    <span className="text-[9px] font-bold">X</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <SourcePicker
              sources={sources}
              usedSourceIds={slots.filter(Boolean)}
              onSelect={(srcId) => setSlotSource(i, srcId)}
              slotIndex={i}
            />
          )}
        </div>
      );
    }
    return slotElements;
  };

  const gridStyle = layout === "split-3"
    ? { display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr" }
    : { display: "grid", gridTemplateColumns: layoutDef.grid, gap: "4px" };

  // For split-3, first source takes full left, 2+3 share right
  const renderSplit3 = () => {
    const src0 = slots[0] ? getSource(slots[0]) : null;
    const src1 = slots[1] ? getSource(slots[1]) : null;
    const src2 = slots[2] ? getSource(slots[2]) : null;

    return (
      <div className="grid grid-cols-2 gap-1 h-full">
        {/* Left: full height */}
        <div className={`relative border-2 rounded-lg overflow-hidden ${slots[0] ? "border-[#0099CC]/50 bg-[#0B1120]" : "border-dashed border-[#2A3441] bg-[#060B14]"}`} data-testid="scene-slot-0">
          {src0 ? (
            <>
              <SlotPreview source={src0} cameraStreams={cameraStreams} audioEnabled={0 === audioSlot} />
              <SlotOverlay name={src0.name} isAudio={0 === audioSlot} onAudioClick={() => setAudioSlot(0)} onRemove={() => setSlotSource(0, null)} />
            </>
          ) : (
            <SourcePicker sources={sources} usedSourceIds={slots.filter(Boolean)} onSelect={(id) => setSlotSource(0, id)} slotIndex={0} />
          )}
        </div>
        {/* Right: two rows */}
        <div className="flex flex-col gap-1">
          {[1, 2].map(i => {
            const src = i === 1 ? src1 : src2;
            return (
              <div key={i} className={`relative flex-1 border-2 rounded-lg overflow-hidden ${slots[i] ? "border-[#0099CC]/50 bg-[#0B1120]" : "border-dashed border-[#2A3441] bg-[#060B14]"}`} data-testid={`scene-slot-${i}`}>
                {src ? (
                  <>
                    <SlotPreview source={src} cameraStreams={cameraStreams} audioEnabled={i === audioSlot} />
                    <SlotOverlay name={src.name} isAudio={i === audioSlot} onAudioClick={() => setAudioSlot(i)} onRemove={() => setSlotSource(i, null)} />
                  </>
                ) : (
                  <SourcePicker sources={sources} usedSourceIds={slots.filter(Boolean)} onSelect={(id) => setSlotSource(i, id)} slotIndex={i} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl overflow-hidden" data-testid="scene-composer">
      {/* Header */}
      <div className="p-3 border-b border-[#1E293B] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LayoutGrid className="w-4 h-4 text-[#EC4899]" />
          <input
            value={sceneName}
            onChange={e => setSceneName(e.target.value)}
            className="bg-transparent text-white text-sm font-semibold border-none outline-none w-28"
            data-testid="scene-name-input"
          />
        </div>
        <div className="flex items-center gap-1">
          {LAYOUTS.map(l => {
            const Icon = l.icon;
            return (
              <button
                key={l.id}
                onClick={() => setLayout(l.id)}
                className={`p-1.5 rounded-md transition-all ${layout === l.id ? "bg-[#EC4899]/20 text-[#EC4899]" : "text-[#64748b] hover:text-white hover:bg-white/5"}`}
                title={l.label}
                data-testid={`scene-layout-${l.id}`}
              >
                <Icon className="w-3.5 h-3.5" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Slots grid */}
      <div className="p-2" style={{ minHeight: layout === "grid-4" ? "200px" : "140px" }}>
        {layout === "split-3" ? renderSplit3() : (
          <div style={gridStyle} className="gap-1 h-full" data-testid="scene-slots-grid">
            {renderSlotGrid()}
          </div>
        )}
      </div>

      {/* Audio info */}
      <div className="px-3 py-1.5 border-t border-[#1E293B] flex items-center gap-2">
        <Volume2 className="w-3 h-3 text-[#10B981]" />
        <span className="text-[9px] text-[#64748b]">
          Audio : {slots[audioSlot] ? getSource(slots[audioSlot])?.name || `Slot ${audioSlot + 1}` : "Aucune source audio"}
          — Les autres sources sont en muet pour eviter les echos
        </span>
      </div>

      {/* Actions */}
      <div className="p-3 border-t border-[#1E293B] flex gap-2">
        <button onClick={onCancel} className="flex-1 px-3 py-1.5 rounded-lg bg-[#1E293B] text-[#94A3B8] text-xs hover:bg-[#2A3441] transition-colors">
          Annuler
        </button>
        <button
          onClick={prepareScene}
          disabled={!slots.slice(0, slotCount).some(Boolean)}
          className="flex-1 px-3 py-1.5 rounded-lg bg-[#EC4899] text-white text-xs font-semibold hover:bg-[#DB2777] transition-colors disabled:opacity-40"
          data-testid="scene-prepare-btn"
        >
          Preparer la scene
        </button>
      </div>
    </div>
  );
}

// ===== SLOT PREVIEW (renders source content in miniature) =====
function SlotPreview({ source, cameraStreams, audioEnabled }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (source.type === "camera") {
      const entry = cameraStreams[source._cameraSourceId || source.config?.source_id];
      if (entry?.stream && videoRef.current) {
        videoRef.current.srcObject = entry.stream;
      }
    }
    return () => { if (videoRef.current) videoRef.current.srcObject = null; };
  }, [source, cameraStreams]);

  if (source.type === "camera") {
    return <video ref={videoRef} autoPlay playsInline muted={!audioEnabled} className="w-full h-full object-cover" />;
  }
  if (source.type === "image" && source.config?.url) {
    return <img src={source.config.url} alt={source.name} className="w-full h-full object-cover" />;
  }
  if (source.type === "text") {
    return (
      <div className="w-full h-full bg-black flex items-center justify-center p-2">
        <p className="text-white text-[8px] text-center truncate" style={{ color: source.config?.color || "#fff" }}>
          {source.config?.text?.slice(0, 60) || "Texte"}
        </p>
      </div>
    );
  }
  if (source.type === "pdf" && source.config?.pages) {
    const page = source.config.pages[0];
    return <img src={page?.thumb_url || page?.full_url} alt="PDF" className="w-full h-full object-contain bg-black" />;
  }
  if (source.type === "livestats") {
    return (
      <div className="w-full h-full bg-gradient-to-br from-[#0B0F19] to-[#151B2B] flex items-center justify-center">
        <span className="text-[10px] text-[#0099CC] font-bold">LIVE STATS</span>
      </div>
    );
  }

  // Generic fallback
  return (
    <div className="w-full h-full bg-[#0B1120] flex items-center justify-center">
      <span className="text-[9px] text-[#4A5568]">{source.name}</span>
    </div>
  );
}

// ===== SLOT OVERLAY =====
function SlotOverlay({ name, isAudio, onAudioClick, onRemove }) {
  return (
    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1.5 flex items-center justify-between">
      <span className="text-[9px] text-white font-medium truncate">{name}</span>
      <div className="flex items-center gap-1">
        <button onClick={onAudioClick} className={`p-0.5 rounded ${isAudio ? "text-[#10B981]" : "text-[#64748b]"}`}>
          {isAudio ? <Volume2 className="w-2.5 h-2.5" /> : <VolumeX className="w-2.5 h-2.5" />}
        </button>
        <button onClick={onRemove} className="text-red-400 hover:text-red-300 p-0.5">
          <span className="text-[9px] font-bold">X</span>
        </button>
      </div>
    </div>
  );
}

// ===== SOURCE PICKER (dropdown in empty slot) =====
function SourcePicker({ sources, usedSourceIds, onSelect, slotIndex }) {
  const [open, setOpen] = useState(false);
  const available = sources.filter(s => !usedSourceIds.includes(s.id));

  const SOURCE_ICONS_MAP = {
    camera: "🎥", image: "🖼", pdf: "📄", youtube: "▶️", video_url: "🎬",
    local_video: "🎬", text: "📝", livestats: "📊", livekit_speakers: "👥",
    wait: "⏳", black: "⬛", countdown: "⏱",
  };

  return (
    <div className="w-full h-full flex items-center justify-center p-2">
      {!open ? (
        <button onClick={() => setOpen(true)} className="text-[#4A5568] hover:text-[#94A3B8] transition-colors text-center" data-testid={`slot-pick-${slotIndex}`}>
          <div className="w-8 h-8 rounded-full border-2 border-dashed border-[#2A3441] flex items-center justify-center mx-auto mb-1">
            <span className="text-sm">+</span>
          </div>
          <span className="text-[9px]">Choisir source</span>
        </button>
      ) : (
        <div className="w-full max-h-full overflow-y-auto space-y-0.5">
          {available.length === 0 ? (
            <p className="text-[9px] text-[#4A5568] text-center py-2">Aucune source disponible</p>
          ) : available.map(s => (
            <button
              key={s.id}
              onClick={() => { onSelect(s.id); setOpen(false); }}
              className="w-full flex items-center gap-1.5 px-2 py-1 rounded text-left text-[9px] text-[#94A3B8] hover:bg-[#151B2B] hover:text-white transition-colors"
              data-testid={`slot-source-${s.id}`}
            >
              <span>{SOURCE_ICONS_MAP[s.type] || "📦"}</span>
              <span className="truncate">{s.name}</span>
            </button>
          ))}
          <button onClick={() => setOpen(false)} className="w-full text-[9px] text-[#4A5568] hover:text-[#94A3B8] py-1">
            Fermer
          </button>
        </div>
      )}
    </div>
  );
}
